import React, { useState } from 'react';
import { useAuthStore } from '../stores/authStore';
import { useNavigation } from '../shared/hooks/useNavigation';
import { ROUTES } from '../shared/navigation';
import { updateUserProfile } from '../services/userService';
import { ArrowLeft, User, Mail, Phone, Save, Calendar, CreditCard, MapPin, Tag, Plus } from 'lucide-react';
import { 
  handleCpfInput, 
  handleCepInput, 
  validateCpfChecksum, 
  validateCepFormat, 
  validateBrazilianState,
  lookupCep,
  BRAZILIAN_STATES,
  cleanFormData,
  validateBrazilianFields
} from '../utils/brazilianUtils';
import LabelChip from '../components/labels/LabelChip';
import LabelSelector from '../components/labels/LabelSelector';
import labelService from '../services/labelService';
import { areLabelsEqual, extractLabelIds } from '../utils/labelUtils';

const EditProfilePage = () => {
  const { navigate, navigateBack } = useNavigation();
  const { user, setProfile } = useAuthStore();
  const [isSaving, setIsSaving] = useState(false);
  const [isLabelSelectorOpen, setIsLabelSelectorOpen] = useState(false);
  const [selectedLabels, setSelectedLabels] = useState(user?.labels || []);

  const [formData, setFormData] = useState({
    fullName: user?.fullName || '',
    email: user?.email || '',
    phone_number: user?.phone_number || '',
    gender: user?.gender || '',
    date_of_birth: user?.date_of_birth || '',
    // CPF and Address fields
    cpf: user?.cpf || '',
    address_street: user?.address_street || '',
    address_number: user?.address_number || '',
    address_complement: user?.address_complement || '',
    address_neighborhood: user?.address_neighborhood || '',
    address_city: user?.address_city || '',
    address_state: user?.address_state || '',
    address_cep: user?.address_cep || '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLabelRemove = async (labelToRemove) => {
    try {
      setSelectedLabels(prev => prev.filter(label => label.id !== labelToRemove.id));
      await labelService.removeLabelFromUser(user.id, labelToRemove.id);
    } catch (error) {
      // Revert on error
      setSelectedLabels(prev => [...prev, labelToRemove]);
      alert('Erro ao remover label. Tente novamente.');
    }
  };

  const handleLabelSelectionChange = (newLabels) => {
    setSelectedLabels(newLabels);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    // Validate Brazilian fields
    const brazilianErrors = validateBrazilianFields(formData);
    if (Object.keys(brazilianErrors).length > 0) {
      alert(`Erro de validação: ${Object.values(brazilianErrors).join(', ')}`);
      return;
    }
    
    setIsSaving(true);
    
    try {
      // Clean and format Brazilian data before submission
      const cleanedData = cleanFormData(formData);
      
      const updatedProfile = await updateUserProfile(cleanedData);
      
      if (!updatedProfile) {
        throw new Error('Failed to update profile - no data returned');
      }
      
      // Update labels if they changed
      try {
        // Check if labels actually changed using utility function
        const labelsChanged = !areLabelsEqual(user?.labels, selectedLabels);
        
        if (labelsChanged) {
          const labelIds = extractLabelIds(selectedLabels);
          await labelService.updateUserLabels(user.id, labelIds);
          updatedProfile.labels = selectedLabels;
        }
      } catch (error) {
        alert('Perfil atualizado, mas houve erro ao salvar labels. Tente novamente.');
      }
      
      // Update the auth store with the new profile data
      setProfile(updatedProfile);
      
      // Navigate back to profile page
      navigate(ROUTES.PROFILE);
      
    } catch (error) {
      alert(`Erro ao salvar perfil: ${error.message || 'Tente novamente.'}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-brand-light dark:bg-brand-dark">
      {/* Header - Luxury Design */}
      <div className="flex items-center p-6 bg-brand-dark dark:bg-brand-light border-b border-brand-light/10 dark:border-brand-dark/10">
        <button onClick={() => navigateBack(ROUTES.PROFILE)} className="p-2 border border-brand-light/20 dark:border-brand-dark/20 hover:bg-brand-light/10 dark:hover:bg-brand-dark/10 transition-all duration-300">
          <ArrowLeft size={22} className="text-brand-light dark:text-brand-dark" strokeWidth={1.5} />
        </button>
        <h1 className="text-xl font-serif font-bold text-brand-light dark:text-brand-dark ml-4 tracking-wide">Editar Perfil</h1>
      </div>

      {/* Form */}
      <div className="flex-1 overflow-y-auto p-6 mobile-scroll">
        <form onSubmit={handleSave} className="space-y-6 pb-6">
          {/* Full Name */}
          <div>
            <label htmlFor="fullName" className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
              Nome Completo
            </label>
            <div className="relative">
              <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-dark/40 dark:text-brand-light/40" strokeWidth={1.5} />
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                className="w-full pl-12 pr-4 py-3 border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                placeholder="Seu nome completo"
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
              Email
            </label>
            <div className="relative">
              <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-dark/40 dark:text-brand-light/40" strokeWidth={1.5} />
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full pl-12 pr-4 py-3 border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                placeholder="seu@email.com"
              />
            </div>
          </div>

          {/* Phone Number */}
          <div>
            <label htmlFor="phone_number" className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
              Telefone
            </label>
            <div className="relative">
              <Phone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-dark/40 dark:text-brand-light/40" strokeWidth={1.5} />
              <input
                type="tel"
                id="phone_number"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleInputChange}
                className="w-full pl-12 pr-4 py-3 border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                placeholder="(11) 99999-9999"
              />
            </div>
          </div>


          {/* Gender */}
          <div>
            <label htmlFor="gender" className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
              Gênero
            </label>
            <div className="relative">
              <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-dark/40 dark:text-brand-light/40" strokeWidth={1.5} />
              <select
                id="gender"
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
                className="w-full pl-12 pr-4 py-3 border border-brand-dark/20 dark:border-brand-light/20 bg-brand-light dark:bg-brand-dark text-brand-dark dark:text-brand-light focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
              >
                <option value="">Selecione...</option>
                <option value="MASCULINO">Masculino</option>
                <option value="FEMININO">Feminino</option>
                <option value="OUTROS">Outro</option>
              </select>
            </div>
          </div>

          {/* Date of Birth */}
          <div>
            <label htmlFor="date_of_birth" className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
              Data de Nascimento
            </label>
            <div className="relative">
              <Calendar size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-dark/40 dark:text-brand-light/40" strokeWidth={1.5} />
              <input
                type="date"
                id="date_of_birth"
                name="date_of_birth"
                value={formData.date_of_birth}
                onChange={handleInputChange}
                className="w-full pl-12 pr-4 py-3 border border-brand-dark/20 dark:border-brand-light/20 bg-brand-light dark:bg-brand-dark text-brand-dark dark:text-brand-light focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
              />
            </div>
          </div>

          {/* CPF */}
          <div>
            <label htmlFor="cpf" className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
              CPF
            </label>
            <div className="relative">
              <CreditCard size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-dark/40 dark:text-brand-light/40" strokeWidth={1.5} />
              <input
                type="text"
                id="cpf"
                name="cpf"
                value={formData.cpf}
                onChange={(e) => {
                  const formatted = handleCpfInput(e.target.value);
                  setFormData(prev => ({ ...prev, cpf: formatted }));
                }}
                className="w-full pl-12 pr-4 py-3 border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                placeholder="000.000.000-00"
                maxLength={14}
              />
            </div>
          </div>

          {/* Address Section */}
          <div className="border-t border-brand-dark/10 dark:border-brand-light/10 pt-6 mt-6">
            <h3 className="text-base font-serif font-bold text-brand-dark dark:text-brand-light mb-4 flex items-center gap-2 uppercase tracking-wide">
              <MapPin size={18} className="text-brand-dark dark:text-brand-light" strokeWidth={1.5} />
              Endereço
            </h3>

            {/* CEP */}
            <div className="mb-4">
              <label htmlFor="address_cep" className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
                CEP
              </label>
              <input
                type="text"
                id="address_cep"
                name="address_cep"
                value={formData.address_cep}
                onChange={(e) => {
                  const formatted = handleCepInput(e.target.value);
                  setFormData(prev => ({ ...prev, address_cep: formatted }));
                }}
                onBlur={async (e) => {
                  const cep = e.target.value;
                  if (cep && validateCepFormat(cep)) {
                    const addressData = await lookupCep(cep);
                    if (addressData) {
                      setFormData(prev => ({
                        ...prev,
                        address_street: addressData.address_street || prev.address_street,
                        address_complement: addressData.address_complement || prev.address_complement,
                        address_neighborhood: addressData.address_neighborhood || prev.address_neighborhood,
                        address_city: addressData.address_city || prev.address_city,
                        address_state: addressData.address_state || prev.address_state,
                      }));
                    }
                  }
                }}
                className="w-full px-4 py-3 border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                placeholder="00000-000"
                maxLength={9}
              />
            </div>

            {/* Street and Number */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="md:col-span-2">
                <label htmlFor="address_street" className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
                  Rua/Logradouro
                </label>
                <input
                  type="text"
                  id="address_street"
                  name="address_street"
                  value={formData.address_street}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                  placeholder="Nome da rua"
                />
              </div>
              <div>
                <label htmlFor="address_number" className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
                  Número
                </label>
                <input
                  type="text"
                  id="address_number"
                  name="address_number"
                  value={formData.address_number}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                  placeholder="123"
                />
              </div>
            </div>

            {/* Complement and Neighborhood */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="address_complement" className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
                  Complemento
                </label>
                <input
                  type="text"
                  id="address_complement"
                  name="address_complement"
                  value={formData.address_complement}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                  placeholder="Apto, Bloco, etc."
                />
              </div>
              <div>
                <label htmlFor="address_neighborhood" className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
                  Bairro
                </label>
                <input
                  type="text"
                  id="address_neighborhood"
                  name="address_neighborhood"
                  value={formData.address_neighborhood}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                  placeholder="Nome do bairro"
                />
              </div>
            </div>

            {/* City and State */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="address_city" className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
                  Cidade
                </label>
                <input
                  type="text"
                  id="address_city"
                  name="address_city"
                  value={formData.address_city}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                  placeholder="Nome da cidade"
                />
              </div>
              <div>
                <label htmlFor="address_state" className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
                  Estado
                </label>
                <select
                  id="address_state"
                  name="address_state"
                  value={formData.address_state}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-brand-dark/20 dark:border-brand-light/20 bg-brand-light dark:bg-brand-dark text-brand-dark dark:text-brand-light focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                >
                  <option value="">Selecione...</option>
                  {BRAZILIAN_STATES.map(state => (
                    <option key={state.code} value={state.code}>
                      {state.code} - {state.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Labels Section */}
          <div className="border-t border-brand-dark/10 dark:border-brand-light/10 pt-6 mt-6">
            <h3 className="text-base font-serif font-bold text-brand-dark dark:text-brand-light mb-4 flex items-center gap-2 uppercase tracking-wide">
              <Tag size={18} className="text-brand-dark dark:text-brand-light" strokeWidth={1.5} />
              Preferências
            </h3>

            {/* Current Labels */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-3 uppercase tracking-wider">
                Preferências Atuais
              </label>
              {selectedLabels.length > 0 ? (
                <div className="flex flex-wrap gap-2 mb-3">
                  {selectedLabels.map((label) => (
                    <LabelChip
                      key={label.id}
                      label={label}
                      size="medium"
                      showRemove={true}
                      onRemove={handleLabelRemove}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-brand-dark/60 dark:text-brand-light/60 text-sm mb-3 tracking-wide">Nenhuma preferência selecionada</p>
              )}

              {/* Add Label Button */}
              <button
                type="button"
                onClick={() => setIsLabelSelectorOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-2 border border-brand-dark/20 dark:border-brand-light/20 hover:bg-brand-dark/5 dark:hover:bg-brand-light/5 text-brand-dark dark:text-brand-light transition-all duration-300 uppercase tracking-wider text-xs"
              >
                <Plus size={14} strokeWidth={1.5} />
                Adicionar Preferências
              </button>
            </div>
          </div>

          <div className="pt-4">
            <button
              type="submit"
              disabled={isSaving}
              className="w-full flex items-center justify-center gap-2 bg-brand-dark dark:bg-brand-light text-brand-light dark:text-brand-dark font-medium py-4 px-4 border border-brand-dark dark:border-brand-light hover:bg-brand-dark/90 dark:hover:bg-brand-light/90 transition-all duration-300 disabled:bg-brand-dark/30 dark:disabled:bg-brand-light/30 disabled:cursor-not-allowed uppercase tracking-widest text-sm"
            >
              {isSaving ? (
                <>
                  <div className="w-5 h-5 border-2 border-brand-light dark:border-brand-dark border-t-transparent animate-spin"></div>
                  Salvando...
                </>
              ) : (
                <>
                  <Save size={18} strokeWidth={1.5} />
                  Salvar Alterações
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Label Selector Modal */}
      <LabelSelector
        isOpen={isLabelSelectorOpen}
        onClose={() => setIsLabelSelectorOpen(false)}
        onSelectionChange={handleLabelSelectionChange}
        selectedLabels={selectedLabels}
        title="Selecionar Preferências"
        allowMultiple={true}
      />
    </div>
  );
};

export default EditProfilePage;
